import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSettings, FiCpu, FiTool, FiSliders, FiSun, FiMoon, FiMonitor } from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';

function Settings() {
  const navigate = useNavigate();
  const { themePreference, setTheme, isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [aiSettings, setAiSettings] = useState({
    ai_integration_id: null,
    available_integrations: []
  });
  
  const [formData, setFormData] = useState({
    ai_integration_id: null
  });

  useEffect(() => {
    loadSettings();
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadSettings();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tenants/current/ai-settings');
      setAiSettings(response.data);
      setFormData({
        ai_integration_id: response.data.ai_integration_id
      });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleIntegrationChange = (e) => {
    const integrationId = e.target.value ? parseInt(e.target.value) : null;
    setFormData({
      ai_integration_id: integrationId
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      await api.put('/tenants/current/ai-settings', formData);
      
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      await loadSettings();
      
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.error || 'Failed to save settings' 
      });
    } finally {
      setSaving(false);
    }
  };

  const themeButtonStyle = (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    border: isActive ? '2px solid var(--primary-600)' : '2px solid var(--gray-200)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: isActive ? 'var(--primary-50)' : 'var(--gray-50)',
    color: isActive ? 'var(--primary-600)' : 'var(--gray-500)'
  });

  if (loading) {
    return (
      <div>
        <nav className="breadcrumb">
          <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">
            Dashboard
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Settings</span>
        </nav>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Settings</span>
      </nav>

      {message.text && (
        <div style={{
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          fontSize: '0.9rem',
          background: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
          border: message.type === 'success' ? '1px solid #c3e6cb' : '1px solid #f5c6cb'
        }}>
          {message.text}
        </div>
      )}

      {/* Theme Settings */}
      <div className="card" style={{ marginBottom: '1.5rem', background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: isDark ? '#f9fafb' : 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <FiSliders /> Appearance
            </h2>
            <p style={{
              color: isDark ? '#9ca3af' : 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Customize how the application looks
            </p>
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            fontWeight: 600,
            color: isDark ? '#e5e7eb' : 'var(--gray-700)',
            marginBottom: '0.75rem',
            fontSize: '0.9rem'
          }}>
            Theme
          </label>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <div 
              onClick={() => setTheme('light')}
              style={{ textAlign: 'center', cursor: 'pointer' }}
            >
              <div style={themeButtonStyle(themePreference === 'light')}>
                <FiSun size={24} />
              </div>
              <span style={{ 
                fontSize: '0.75rem', 
                color: themePreference === 'light' ? 'var(--primary-600)' : 'var(--gray-500)',
                fontWeight: themePreference === 'light' ? 600 : 400,
                marginTop: '0.5rem',
                display: 'block'
              }}>Light</span>
            </div>
            <div 
              onClick={() => setTheme('dark')}
              style={{ textAlign: 'center', cursor: 'pointer' }}
            >
              <div style={themeButtonStyle(themePreference === 'dark')}>
                <FiMoon size={24} />
              </div>
              <span style={{ 
                fontSize: '0.75rem', 
                color: themePreference === 'dark' ? 'var(--primary-600)' : 'var(--gray-500)',
                fontWeight: themePreference === 'dark' ? 600 : 400,
                marginTop: '0.5rem',
                display: 'block'
              }}>Dark</span>
            </div>
            <div 
              onClick={() => setTheme('system')}
              style={{ textAlign: 'center', cursor: 'pointer' }}
            >
              <div style={themeButtonStyle(themePreference === 'system')}>
                <FiMonitor size={24} />
              </div>
              <span style={{ 
                fontSize: '0.75rem', 
                color: themePreference === 'system' ? 'var(--primary-600)' : 'var(--gray-500)',
                fontWeight: themePreference === 'system' ? 600 : 400,
                marginTop: '0.5rem',
                display: 'block'
              }}>System</span>
            </div>
          </div>
          <p style={{
            fontSize: '0.8rem',
            color: isDark ? '#9ca3af' : 'var(--gray-500)',
            marginTop: '0.75rem'
          }}>
            Choose your preferred color theme or use your system settings
          </p>
        </div>
      </div>

      {/* AI Provider Settings */}
      <div className="card" style={{ marginBottom: '1.5rem', background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: isDark ? '#f9fafb' : 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <FiCpu /> AI Provider Settings
            </h2>
            <p style={{
              color: isDark ? '#9ca3af' : 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Configure which AI provider and model to use for AI-powered features
            </p>
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            fontWeight: 600,
            color: isDark ? '#e5e7eb' : 'var(--gray-700)',
            marginBottom: '0.5rem',
            fontSize: '0.9rem'
          }}>
            AI Integration
          </label>
          <select
            value={formData.ai_integration_id || ''}
            onChange={handleIntegrationChange}
            className="form-select"
            style={{ maxWidth: '400px', backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
          >
            <option value="">Use System Default</option>
            {aiSettings.available_integrations.map(integration => (
              <option key={integration.id} value={integration.id}>
                {integration.display_name} ({integration.platform.toUpperCase()})
              </option>
            ))}
          </select>
          <p style={{
            fontSize: '0.8rem',
            color: isDark ? '#9ca3af' : 'var(--gray-500)',
            marginTop: '0.5rem'
          }}>
            Select an AI integration configured in the Integrations page.
            {aiSettings.available_integrations.length === 0 && (
              <> <a href="/integrations" style={{ color: 'var(--primary-600)' }}>
                Add an AI integration →
              </a></>
            )}
          </p>
        </div>

        {formData.ai_integration_id && (
          <div style={{ 
            padding: '1rem', 
            background: isDark ? '#1e3a5f' : '#f0f9ff', 
            border: `1px solid ${isDark ? '#3b82f6' : '#bfdbfe'}`,
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {(() => {
              const integration = aiSettings.available_integrations.find(i => i.id === formData.ai_integration_id);
              return (
                <p style={{ margin: 0, color: isDark ? '#93c5fd' : '#1e40af', fontSize: '0.9rem' }}>
                  ℹ️ Using AI integration: <strong>{integration?.display_name}</strong>
                  <br />
                  Provider: <strong>{integration?.platform || 'unknown'}</strong> | Model: <strong>{integration?.settings?.model || 'not set'}</strong>
                </p>
              );
            })()}
          </div>
        )}

        {!formData.ai_integration_id && (
          <div style={{ 
            padding: '1rem', 
            background: isDark ? '#78350f' : '#fff3cd', 
            border: `1px solid ${isDark ? '#fbbf24' : '#ffc107'}`,
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            <p style={{ margin: 0, color: isDark ? '#fde68a' : '#856404', fontSize: '0.9rem' }}>
              ⚠️ No AI integration selected. Please configure an AI integration in the <a href="/integrations" style={{ color: 'var(--primary-600)', fontWeight: 600 }}>Integrations page</a> and select it here.
            </p>
          </div>
        )}

        <button 
          className="btn btn-primary" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* External Tools */}
      <div className="card" style={{ marginBottom: '1.5rem', background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: isDark ? '#f9fafb' : 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <FiTool /> External Tools
            </h2>
            <p style={{
              color: isDark ? '#9ca3af' : 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Access external tools and integrations for advanced automation
            </p>
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{
            display: 'block',
            fontWeight: 600,
            color: isDark ? '#e5e7eb' : 'var(--gray-700)',
            marginBottom: '0.5rem',
            fontSize: '0.9rem'
          }}>
            n8n Workflow Automation
          </label>
          <p style={{
            fontSize: '0.875rem',
            color: isDark ? '#9ca3af' : 'var(--gray-600)',
            marginBottom: '1rem'
          }}>
            Create and manage advanced marketing automation workflows using n8n.
          </p>
          <a 
            href={`${window.location.protocol}//n8n.${window.location.host.replace(/^[^.]+\./, '')}`}
            target="_blank" 
            rel="noopener noreferrer"
            className="btn btn-primary"
            style={{ textDecoration: 'none' }}
          >
            Open n8n Workflow Editor →
          </a>
          <p style={{
            fontSize: '0.75rem',
            color: isDark ? '#9ca3af' : 'var(--gray-500)',
            marginTop: '0.75rem'
          }}>
            Login: admin / aibos_dev_password
          </p>
        </div>
      </div>

      {/* Other Settings */}
      <div className="card" style={{ background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: isDark ? '#f9fafb' : 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <FiSettings /> Other Settings
            </h2>
            <p style={{
              color: isDark ? '#9ca3af' : 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Additional configuration options coming soon
            </p>
          </div>
        </div>

        <p style={{ color: isDark ? '#9ca3af' : 'var(--gray-500)', fontStyle: 'italic' }}>
          User preferences, notifications, API keys, and more will be available here.
        </p>
      </div>
    </div>
  );
}

export default Settings;
